/**
 * 会员管理系统 - 功能管理 API
 *
 * 提供功能和套餐-功能配额的 CRUD 操作
 */

import httpRequest from '@/utils/api'
import type {
  Feature,
  PlanFeature,
  CreateFeatureRequest,
  UpdateFeatureRequest,
  FeatureListResponseData,
  CreatePlanFeatureRequest,
  UpdatePlanFeatureRequest,
  PlanFeaturesResponseData,
} from '@/types/membership/feature'

enum API {
  features = '/admin/features',
  planFeatures = '/admin/plans',
}

/**
 * 获取所有功能
 *
 * @returns 功能列表响应
 */
export const getAllFeaturesAPI = () => {
  return httpRequest.get<FeatureListResponseData>({
    url: API.features,
  })
}

/**
 * 创建功能
 *
 * @param data 创建功能请求数据
 * @returns 功能详情响应
 */
export const createFeatureAPI = (data: CreateFeatureRequest) => {
  return httpRequest.post<Feature>({
    url: API.features,
    data,
  })
}

/**
 * 更新功能
 *
 * @param featureId 功能 ID
 * @param data 更新功能请求数据
 * @returns 功能详情响应
 */
export const updateFeatureAPI = (featureId: number, data: UpdateFeatureRequest) => {
  return httpRequest.put<Feature>({
    url: `${API.features}/${featureId}`,
    data,
  })
}

/**
 * 删除功能
 *
 * @param featureId 功能 ID
 * @returns 删除响应
 */
export const deleteFeatureAPI = (featureId: number) => {
  return httpRequest.delete<{ message: string }>({
    url: `${API.features}/${featureId}`,
  })
}

/**
 * 获取套餐的所有功能
 *
 * @param planId 套餐 ID
 * @returns 套餐功能列表响应
 */
export const getPlanFeaturesAPI = (planId: number) => {
  return httpRequest.get<PlanFeaturesResponseData>({
    url: `${API.planFeatures}/${planId}/features`,
  })
}

/**
 * 为套餐添加功能
 *
 * @param planId 套餐 ID
 * @param data 创建套餐功能请求数据
 * @returns 套餐功能详情响应
 */
export const addPlanFeatureAPI = (planId: number, data: CreatePlanFeatureRequest) => {
  return httpRequest.post<PlanFeature>({
    url: `${API.planFeatures}/${planId}/features`,
    data,
  })
}

/**
 * 更新套餐功能配额
 *
 * @param planId 套餐 ID
 * @param pfId 套餐功能 ID
 * @param data 更新套餐功能请求数据
 * @returns 套餐功能详情响应
 */
export const updatePlanFeatureAPI = (
  planId: number,
  pfId: number,
  data: UpdatePlanFeatureRequest
) => {
  return httpRequest.put<PlanFeature>({
    url: `${API.planFeatures}/${planId}/features/${pfId}`,
    data,
  })
}

/**
 * 移除套餐功能
 *
 * @param planId 套餐 ID
 * @param pfId 套餐功能 ID
 * @returns 删除响应
 */
export const removePlanFeatureAPI = (planId: number, pfId: number) => {
  return httpRequest.delete<{ message: string }>({
    url: `${API.planFeatures}/${planId}/features/${pfId}`,
  })
}

